<template>
  <div class="bg-white bottom-0 left-0 right-0">
    <div v-show="showSaveNewFilterContainer" class="save-new-filter-container">
      <div class="input-filter-name-section">
        <label for="inputElementFilterName" class="label">
          {{ $t("appliedFilters.filterName") }}
        </label>
        <input
          ref="inputElementFilterName"
          v-model="inputValueNewFilterName"
          class="input-box"
          autofocus
          :placeholder="$t('appliedFilters.enterNewFilterName')"
          @input="handleInput"
        />
        <div v-if="showInputErrorMessage" class="py-2 text-sm text-danger-600">
          {{ $t("appliedFilters.errMsg") }}
        </div>
      </div>
      <div class="content-row-visibility">
        {{ $t("appliedFilters.visibility") }}:
        <el-radio-group
          v-model="selectedVisibilityOption"
          class="flex flex-col items-start gap-2 mt-2 w-full"
          @change="onVisibilityChange"
        >
          <el-radio label="private" class="w-full !mr-0 flex items-center">
            {{ $t("appliedFilters.privateToMe") }}
          </el-radio>
          <el-radio label="shared" class="w-full !mr-0 flex items-center">
            {{ $t("appliedFilters.shareWithTeam") }}
          </el-radio>
        </el-radio-group>
      </div>
      <div class="save-new-filter-buttons">
        <PrimaryButton
          variant="light"
          :custom-class="'h-[40px] w-[184px]'"
          @on-click="closeSaveNewFilterContainer"
        >
          <span>{{ $t("savedSearch.cancel") }}</span>
        </PrimaryButton>
        <PrimaryButton
          variant="primary"
          :custom-class="'h-[40px] w-[184px] !text-sm !font-semibold'"
          :disabled="isSaveNewFilterButtonDisabled"
          @on-click="createNewSearchFilter"
        >
          <span>{{ $t("appliedFilters.saveNewFilter") }}</span>
        </PrimaryButton>
      </div>
    </div>
    <div>
      <div v-show="showApplyContainer" class="apply-container">
        <div class="header-row font-subtitle-2-strong">
          {{ $t("appliedFilters.chooseAction") }}
          <font-awesome-icon
            class="close-btn"
            :icon="['fas', 'xmark']"
            @click="closeApplyContainer"
          />
        </div>
        <div class="content-row">
          <div class="content-row-action-buttons">
            <PrimaryButton
              variant="light"
              :custom-class="'h-[40px] w-[184px]'"
              @on-click="openSaveNewFilterContainer"
            >
              <span>{{ $t("appliedFilters.saveAsNew") }} </span>
            </PrimaryButton>
            <PrimaryButton
              variant="primary"
              :custom-class="'h-[40px] w-[184px] !text-sm !font-semibold'"
              :disabled="!Boolean(searchFilterStore?.currentSearchFilter?.id)"
              @on-click="showUpdateSearchFilter"
            >
              <span>{{ $t("appliedFilters.updateChanges") }} </span>
            </PrimaryButton>
          </div>
          <div
            v-show="!searchFilterStore.isCurrentFilterDefault"
            class="content-row-visibility"
          >
            {{ $t("appliedFilters.visibility") }}:
            <el-radio-group
              v-model="selectedVisibilityOption"
              class="flex flex-col items-start gap-2 mt-2 w-full"
              @change="onVisibilityChange"
            >
              <el-radio label="private" class="w-full !mr-0 flex items-center">
                {{ $t("appliedFilters.privateToMe") }}
              </el-radio>
              <el-radio label="shared" class="w-full !mr-0 flex items-center">
                {{ $t("appliedFilters.shareWithTeam") }}
              </el-radio>
            </el-radio-group>
          </div>
        </div>
      </div>
      <div class="container">
        <PrimaryButton
          data-target="Reset Changes filter button"
          variant="secondary"
          :custom-class="'h-[40px]'"
          :disabled="disableResetButton"
          @on-click="resetSearchFilter"
        >
          <span class="flex justify-center items-center gap-2">
            <font-awesome-icon :icon="['far', 'arrows-rotate']" class="icon" />
            <span class="whitespace-nowrap">{{
              $t("appliedFilters.resetChanges")
            }}</span>
          </span>
        </PrimaryButton>
        <PrimaryButton
          data-target="Apply filter button"
          variant="primary"
          :custom-class="'h-[40px] w-[66px]'"
          :disabled="disableApplyButton"
          @on-click="onClickApply"
        >
          <span class="flex justify-center items-center gap-2">
            <span>{{ `${$t(`common.save`)}` }}</span>
          </span>
        </PrimaryButton>
      </div>
    </div>
    <Dialog
      v-if="showUpdateDefaultFilterDialog"
      :data-target="'Update Default Filter'"
      :dialog-title="
        $t('appliedFilters.namedDefaultFilter', {
          searchName: savedSearchStore.selectedNestedSearch?.name,
        })
      "
      :confirm-button-text="$t('appliedFilters.continueUpdate')"
      :has-cancel-button="false"
      :has-exit-button="true"
      :min-width="'25%'"
      :max-width="'40%'"
      :is-button-disabled="false"
      :display-checkbox="true"
      :checkbox-label="$t('dialog.doNotShowAgain')"
      @cancel="showUpdateDefaultFilterDialog = false"
      @confirm="updateSearchFilter"
      @checkbox-clicked="setDoNotShowAgainDialogValue"
    >
      <div>
        <p>{{ $t("appliedFilters.updateDefaultFilterPopup") }}</p>
      </div>
    </Dialog>
  </div>
</template>

<script setup lang="ts">
import { Ref, ref, watch, computed } from "vue";
import { ElNotification } from "element-plus";
import { useI18n } from "vue-i18n";
import Dialog from "~/components/shared/custom-dialog/CustomDialog.vue";
import PrimaryButton from "~/components/shared/primary-button/PrimaryButton.vue";
import { useSearchStore } from "~/store/search";
import { useSearchFiltersStore } from "~/store/searchFilter";
import SearchFilterAttributes from "~/types/attributes/SearchFilterAttributesType";

const emit = defineEmits(["handle-save"]);

const { t } = useI18n();
const savedSearchStore = useSearchStore();
const searchFilterStore = useSearchFiltersStore();

const showApplyContainer: Ref<boolean> = ref(false);
const showSaveNewFilterContainer: Ref<boolean> = ref(false);
const isPendingRequest: Ref<boolean> = ref(false);
const selectedVisibilityOption: Ref<string> = ref("");
const inputValueNewFilterName: Ref<string> = ref("");
const inputElementFilterName: Ref<HTMLInputElement | null> = ref(null);
const showUpdateDefaultFilterDialog: Ref<boolean> = ref(false);
const showInputErrorMessage: Ref<boolean> = ref(false);

let undoClicked = false;

const props = defineProps({
  isEditing: {
    type: Boolean,
    default: false,
  },
  searchSaveDisabled: {
    type: Boolean,
    default: false,
  },
});

const onSearchPage = computed(() => {
  return savedSearchStore.onSearchPageCheck;
});

const disableResetButton = computed(() => {
  return (
    !searchFilterStore.hasCurrentSearchFilterChanged ||
    isPendingRequest.value === true
  );
});

const disableApplyButton = computed(() => {
  if (onSearchPage.value && !searchFilterStore.currentSearchFilter?.name) {
    /*
      On the search page, we can save if
      there has been a change to the filter
      or to the search itself. Up until the
      search is saved and filter created,
      allow the MentionsTableToolbar to control
      the disabled state based on search values.
    */
    return props.searchSaveDisabled;
  }
  return (
    (props.isEditing ||
      searchFilterStore.currentSearchFilter?.name?.trim() === "" ||
      !searchFilterStore.hasCurrentSearchFilterChanged ||
      isPendingRequest.value === true) &&
    !savedSearchStore.hasUnsavedChanges
  );
});

const onClickApply = () => {
  if (!onSearchPage.value) {
    openApplyContainer();
  } else {
    // On the search page, save the search and filter
    emit("handle-save");
    updateSearchFilter();
  }
};

const openApplyContainer = () => {
  showApplyContainer.value = true;
  selectedVisibilityOption.value =
    searchFilterStore?.currentSearchFilter?.visibility ?? "";
};

const closeApplyContainer = () => {
  showApplyContainer.value = false;
};

const openSaveNewFilterContainer = () => {
  closeApplyContainer();
  showSaveNewFilterContainer.value = true;
  inputValueNewFilterName.value = "";
};

const closeSaveNewFilterContainer = () => {
  showSaveNewFilterContainer.value = false;
  openApplyContainer();
};

const onVisibilityChange = (newVisibilityValue: string) => {
  if (
    searchFilterStore.currentSearchFilter &&
    searchFilterStore.currentSearchFilter.visibility !== "profile"
  ) {
    searchFilterStore.currentSearchFilter.visibility = newVisibilityValue;
  }
};

const isSaveNewFilterButtonDisabled = computed(() => {
  return (
    (selectedVisibilityOption.value !== "private" &&
      selectedVisibilityOption.value !== "shared") ||
    inputValueNewFilterName.value.trim() === ""
  );
});

// Watch for changes in getCurrentSearchFilter
watch(
  () => searchFilterStore.currentSearchFilter,
  (newValue: SearchFilterAttributes | null) => {
    // Update refs based on the new value
    selectedVisibilityOption.value = newValue?.visibility ?? "";
  },
);

const handleInput = (event: Event) => {
  inputValueNewFilterName.value = (event.target as HTMLInputElement).value;
  const hasDuplicate = searchFilterStore.getUserSearchFilters.some(
    (filter: SearchFilterAttributes) =>
      filter.name === inputValueNewFilterName.value.trim(),
  );

  showInputErrorMessage.value = hasDuplicate;
};

const showUpdateSearchFilter = () => {
  const storedValue = localStorage.getItem("doNotShowUpdateDialog");
  const doNotShowUpdateDialog = storedValue ? JSON.parse(storedValue) : false;

  closeApplyContainer();

  if (
    searchFilterStore.isCurrentFilterDefault &&
    !doNotShowUpdateDialog &&
    !savedSearchStore.hasLicenseLimitsChanged
  ) {
    showUpdateDefaultFilterDialog.value = true;
  } else {
    updateSearchFilter();
  }
};

const resetSearchFilter = () => {
  searchFilterStore.resetSearchFilter = true;
  searchFilterStore.resetSearchFilters();
  closeApplyContainer();
};

const updateSearchFilter = () => {
  if (savedSearchStore.isNotAccesptedSearch) {
    return;
  }
  isPendingRequest.value = true;
  showUpdateDefaultFilterDialog.value = false;

  const notification = ElNotification({
    customClass: "notification-xs success",
    dangerouslyUseHTMLString: true,
    message: `
      <div class="flex items-center gap-2">
        <div class="w-4 h-4 flex items-center justify-center">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
            <path fill="#059669" d="M256 48a208 208 0 1 1 0 416 208 208 0 1 1 0-416zm0 464A256 256 0 1 0 256 0a256 256 0 1 0 0 512zM369 209c9.4-9.4 9.4-24.6 0-33.9s-24.6-9.4-33.9 0l-111 111-47-47c-9.4-9.4-24.6-9.4-33.9 0s-9.4 24.6 0 33.9l64 64c9.4 9.4 24.6 9.4 33.9 0L369 209z"/>
          </svg>
        </div>
        <span class="flex-1 text-slate-900 font-sans text-xs not-italic font-normal">${t("savedSearch.searchSaveUpdate")}</span>
        <button id="updateSearchFilterButton" class="text-slate-900 font-sans text-xs not-italic font-semibold">${t("common.undo")}</button>
      </div>
    `,
    position: "bottom-left",
    duration: 5000,
    offset: 50,
    onClose: async () => {
      isPendingRequest.value = false;

      if (undoClicked) {
        undoClicked = false;
      } else {
        await searchFilterStore.updateSearchFilter();
      }
    },
  });
  // Add a callback to the 'UNDO' button
  const button = document.getElementById(`updateSearchFilterButton`);
  if (button) {
    button.addEventListener("click", () => {
      undoClicked = true;
      notification.close();
    });
  }
};

const createNewSearchFilter = () => {
  if (inputValueNewFilterName.value === "" || showInputErrorMessage.value) {
    return;
  }

  isPendingRequest.value = true;
  showSaveNewFilterContainer.value = false;

  const notification = ElNotification({
    customClass: "notification-xs success",
    dangerouslyUseHTMLString: true,
    message: `
      <div class="flex items-center gap-2">
        <div class="w-4 h-4 flex items-center justify-center">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
            <path fill="#059669" d="M256 48a208 208 0 1 1 0 416 208 208 0 1 1 0-416zm0 464A256 256 0 1 0 256 0a256 256 0 1 0 0 512zM369 209c9.4-9.4 9.4-24.6 0-33.9s-24.6-9.4-33.9 0l-111 111-47-47c-9.4-9.4-24.6-9.4-33.9 0s-9.4 24.6 0 33.9l64 64c9.4 9.4 24.6 9.4 33.9 0L369 209z"/>
          </svg>
        </div>
        <span class="flex-1 text-slate-900 font-sans text-xs not-italic font-normal">${t("appliedFilters.filterCreated")}</span>
        <button id="createSearchFilterButton" class="text-slate-900 font-sans text-xs not-italic font-semibold">${t("common.undo")}</button>
      </div>
    `,
    position: "bottom-left",
    duration: 5000,
    offset: 50,
    onClose: async () => {
      isPendingRequest.value = false;

      if (undoClicked) {
        undoClicked = false;
        showSaveNewFilterContainer.value = true;
      } else {
        await searchFilterStore.createSearchFilter(
          inputValueNewFilterName.value,
          selectedVisibilityOption.value,
        );
      }
    },
  });

  // Add a callback to the 'UNDO' button
  const button = document.getElementById(`createSearchFilterButton`);
  if (button) {
    button.addEventListener("click", () => {
      undoClicked = true;
      notification.close();
    });
  }
};

const setDoNotShowAgainDialogValue = (value: boolean) => {
  localStorage.setItem("doNotShowUpdateDialog", String(value));
};
</script>

<style scoped>
.apply-container {
  @apply flex flex-col bg-white;
}
.save-new-filter-container {
  @apply flex flex-col bg-accent-100 px-[22px] py-[17px];
}
.header-row {
  @apply flex justify-between items-center bg-accent-100 h-16 px-[22px] py-2.5;
}
.content-row {
  @apply flex flex-col bg-accent-100 px-[22px];
}
.close-btn {
  @apply cursor-pointer h-5 w-5;
}
.content-row-action-buttons {
  @apply flex flex-row justify-end gap-x-[14px] items-center my-4;
}
.content-row-visibility {
  @apply flex flex-col my-2.5 text-gray-900 text-sm font-medium font-['Inter'] leading-tight;
}
.container {
  @apply flex flex-row justify-end gap-x-[14px] items-center px-[22px] py-[19px];
}
.input-filter-name-section {
  @apply flex flex-col gap-1 mb-4;
}
.save-new-filter-buttons {
  @apply flex flex-row justify-end gap-x-[14px] items-center my-4;
}
.button {
  @apply h-fit p-4 cursor-pointer rounded-lg text-[0.875rem] leading-normal;
}
.button-secondary {
  @apply bg-transparent hover:bg-transparent active:bg-transparent focus:bg-transparent text-accent-800 hover:text-success-400 border border-accent-400;
}
.button-primary {
  @apply bg-success-400 hover:bg-success-400 active:bg-success-400 focus:bg-success-400 text-white hover:text-white active:text-white focus:text-white;
}
.icon {
  @apply text-base;
}
.label {
  @apply text-accent-800 text-base font-normal not-italic;
}
.input-box {
  @apply w-full border border-accent-200 focus:border-accent-200 focus:outline-none rounded px-[0.62rem] py-2;
}
</style>

<style>
.el-radio__input + .el-radio__label,
.el-radio__input.is-checked + .el-radio__label {
  @apply text-accent-800 font-normal;
}

.el-radio__input.is-checked .el-radio__inner {
  @apply bg-secondary-700 border-secondary-700 hover:bg-secondary-800 hover:border-secondary-800;
}

.el-radio__inner {
  @apply hover:border-secondary-800;
}
</style>
